import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import LoadingIndicator from '../../components/loadingIndicator'
import { useAuth } from '../../core/AuthContext'
import { useAxios } from '../../core/AxiosContext'
import { useGlobal, useGlobalDispatch } from '../../core/GlobalContext'
import { REQUEST_STATUS, ROUTES } from '../../core/actions'
import { INR } from '../../core/constants'
import indianNumberFormat from '../../utils/indianNumberFormat'
import { customDateFormat, isWithinCancellationWindow } from '../../utils/misc'
import { fetchWalletAction } from '../financier/FinancierDuck'
import * as styles from './Overview.css'
import {
  getPendingWithdrawalRequestsAction,
  refreshPortfolioGraphsAction,
  refreshPortfolioTrackerAction,
  reverseWithdrawalRequestAction,
} from './OverviewDuck'

// Money formatter in Indian grouping. dec=0 for the big display figures, dec=2 for wallet balance.
const money = (amount, dec = 0) => `${INR} ${indianNumberFormat(Number(amount) || 0, dec)}`

export const Overview = () => {
  const { authAxios } = useAxios()
  const { authState } = useAuth()
  const navigate = useNavigate()
  const {
    financierState: { financier, status },
    overviewState: { portfolioTracker, portfolioTrackerStatus, pendingWithdrawalRequests },
  } = useGlobal()
  const { financierDispatch, overviewDispatch, toastDispatch } = useGlobalDispatch()

  useEffect(() => {
    fetchWalletAction()(authAxios, financierDispatch)
  }, [])

  useEffect(() => {
    if (financier?.financierPAN) {
      refreshPortfolio()
    }
  }, [financier?.financierPAN])

  // high_yield-withdrawal-cancel: pending withdrawals the investor can reverse within 3h
  useEffect(() => {
    financier?.walletId && getPendingWithdrawalRequestsAction(financier?.walletId)(authAxios, overviewDispatch)
  }, [financier?.walletId])

  const reversibleWithdrawals = (pendingWithdrawalRequests || []).filter(e => e?.aasm_state === 'initiated')

  const handleDepositClick = () => navigate(ROUTES.DEPOSIT)
  const handleWithdrawClick = () => {
    navigate(ROUTES.WITHDRAW)
  }

  const refreshPortfolio = async () => {
    try {
      await refreshPortfolioGraphsAction(financier?.financierPAN)(authAxios)
    } catch (error) {
      console.error('Failed to refresh portfolio graphs', error)
    } finally {
      refreshPortfolioTrackerAction()(authAxios, overviewDispatch)
    }
  }

  // Raw metrics from portfolio_trackers (kept numeric so we can compose the bar / return %).
  const amountInvested = Number(portfolioTracker?.amountInvested) || 0
  const currentValue = Number(portfolioTracker?.currentInvestedValue) || 0
  const netGain = Number(portfolioTracker?.netGain) || 0
  const netIRR = portfolioTracker?.netIRR

  const isGain = netGain >= 0
  const returnPct = amountInvested > 0 ? (netGain / amountInvested) * 100 : 0
  const returnPctLabel = `${returnPct >= 0 ? '+' : ''}${returnPct.toFixed(2)}%`
  const netIRRLabel = `${(Number(netIRR) || 0).toFixed(2)}%`

  // Composition bar: guard divide-by-zero when current value is 0 (fall back to a flat/empty bar).
  const principalPct = currentValue > 0 ? Math.max(0, Math.min(100, (amountInvested / currentValue) * 100)) : 0
  const gainPct = currentValue > 0 ? Math.max(0, 100 - principalPct) : 0

  const renderHero = () => (
    <>
      <div className={styles.val}>
        <div className={styles.big}>{money(currentValue)}</div>
        <div className={isGain ? styles.change : styles.changeLoss}>
          <span className={styles.tri}>{isGain ? '▲' : '▼'}</span>
          {` ${money(Math.abs(netGain))} · ${returnPctLabel}`}
        </div>
      </div>
      <div className={styles.sub}>Current value of your active investments</div>

      <div className={styles.compose}>
        <div className={styles.bar}>
          <i className={styles.principal} style={{ width: `${principalPct}%` }} />
          <i className={styles.gain} style={{ width: `${gainPct}%` }} />
        </div>
        <div className={styles.legend}>
          <div className={styles.li}>
            <span className={styles.dotPrincipal} />
            <div>
              <div className={styles.lk}>Principal invested</div>
              <div className={styles.lv}>{money(amountInvested)}</div>
            </div>
          </div>
          <div className={styles.li}>
            <span className={styles.dotGain} />
            <div>
              <div className={styles.lk}>Net gain earned</div>
              <div className={styles.lvGain}>{money(netGain)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <div className={styles.k}>Amount invested</div>
          <div className={styles.v}>{money(amountInvested)}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.k}>Net gain</div>
          <div className={styles.vGood}>{`${isGain ? '+' : '−'}${money(Math.abs(netGain))}`}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.k}>Net IRR (p.a.)</div>
          <div className={styles.vRed}>{netIRRLabel}</div>
        </div>
      </div>
    </>
  )

  return (
    <div className={styles.overview}>
      {status === REQUEST_STATUS.PENDING ? (
        <LoadingIndicator />
      ) : (
        <>
          <div className={styles.pagehead}>
            <h1 className={styles.pageTitle}>Overview</h1>
          </div>

          <div className={styles.top}>
            <section className={styles.pf}>
              <div className={styles.eyebrow}>Portfolio</div>
              {portfolioTrackerStatus === REQUEST_STATUS.PENDING ? <LoadingIndicator /> : renderHero()}
            </section>

            <section className={styles.wl}>
              <div className={styles.eyebrow}>Wallet</div>
              <div className={styles.amt}>{money(financier?.walletBalance)}</div>
              <div className={styles.sub}>Available to invest</div>
              <div className={styles.btns}>
                <button type='button' className={styles.dep} onClick={handleDepositClick}>
                  Deposit
                </button>
                <button type='button' className={styles.wd} onClick={handleWithdrawClick}>
                  Withdraw
                </button>
              </div>
              <div className={styles.note}>
                <span className={styles.noteIcon}>{'ⓘ'}</span>
                <span>Reversing a deposit is allowed within 3 hours of the transfer.</span>
              </div>
            </section>
          </div>

          {/* "Wallet transactions" = the withdrawal-reversal feature (reverse a withdrawal
              within 3h). It is NOT a generic ledger — confirmed via git history. */}
          <section className={styles.txn}>
            <div className={styles.txnHead}>
              <h2 className={styles.txnTitle}>Wallet transactions</h2>
              <span className={styles.cap}>
                Reversing transactions is allowed for the first 3 hours from the time of transfer
              </span>
            </div>
            {reversibleWithdrawals.length > 0 ? (
              <div className={styles.pendingList}>
                {reversibleWithdrawals.map(entry => {
                  const canReverse = isWithinCancellationWindow(entry?.transacted_on)
                  return (
                    <div key={entry.id} className={styles.pendingItem}>
                      <div>
                        <div className={styles.k}>Transaction amount</div>
                        <div className={styles.pendingAmt}>{money(entry?.transaction_amount)}</div>
                      </div>
                      <div>
                        <div className={styles.k}>Transacted on</div>
                        <div className={styles.pendingWhen}>{customDateFormat(entry?.transacted_on)}</div>
                      </div>
                      <button
                        type='button'
                        className={styles.reverseBtn}
                        disabled={!canReverse}
                        onClick={() =>
                          reverseWithdrawalRequestAction(financier?.walletId, entry?.id)(
                            authAxios,
                            overviewDispatch,
                            toastDispatch
                          )
                        }>
                        Reverse
                      </button>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className={styles.noTxn}>No recent transactions found.</div>
            )}
          </section>
        </>
      )}
    </div>
  )
}
